import * as Yup from 'yup';
import AllForm from '../../stateless/Form';
import {
  Box,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { useCreateMeal, useEditMeal } from '../../Query/MealQuery';
import { useAllChoiceGroup } from '../../Query/ChoiceGroupQuery';
import { useAtom } from 'jotai';
import { useState, useEffect, useMemo } from 'react';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { FieldProps, FormikProps } from 'formik';
import { mealAtom } from './types';
import { useThemeColors } from '../../../Hooks/useThemeColors';
import { COLORS } from '../../../theme/themeConstants';
import { useTranslation } from 'react-i18next';
import { useStoreManagementParams } from '../ManageChoice/useStoreManagementParams';
import { useGroupChoiceParams } from '../ManageChoiceGroup/useGroupChoiceParams';

interface StoreOption {
  value: string;
  label: string;
  storeType: string;
}



interface ChoiceGroupOption {
  id: string;
  groupNameEnglish: string;
  groupNameArabic: string;
  price?: number;
}

const AddMeal = ({ onCancel }: { onCancel: () => void }): any => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  const [mealDataId] = useAtom(mealAtom);
  const { warning } = useThemeColors();
  const [open, setOpen] = useState(false);

  // Stores for dropdowns
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState(mealDataId?.storeId || '');

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const { mutate: CreateMeal, isPending: CreateMealLoading } = useCreateMeal();
  const { mutate: EditMeal, isPending: EditMealLoading } = useEditMeal();

  // Load stores
  const { params } = useStoreManagementParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => (isReady ? params : {}), [isReady, params]);

  const { data: storesData, isLoading: isStoresLoading } = useAllStores(memoizedParams);

  useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      const options = storesData.data.map((store: any) => ({
        value: store.id,
        label: store.restaurantDetails?.storeName || store.nameEnglish || '',
        storeType: store.restaurantDetails?.storeType || '',
      }));
      setStoreOptions(options);
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);



  // Choice group management params - only fetch when store is selected
  const { params: groupParams } = useGroupChoiceParams();
  const isGroupReady = Boolean(groupParams && Object.keys(groupParams).length > 0);
  const shouldFetchChoiceGroups = !!selectedStoreId;

  const memoizedGroupParams = useMemo(() => {
    if (!shouldFetchChoiceGroups) {
      return {};
    }
    const params: any = { ...groupParams, storeId: selectedStoreId };
    // Only include search if it's not empty
    if (groupParams?.search && groupParams.search.trim() !== '') {
      params.search = groupParams.search;
    }
    return isGroupReady ? params : { storeId: selectedStoreId };
  }, [isGroupReady, groupParams, selectedStoreId, shouldFetchChoiceGroups]);

  // Fetch choice groups only when store is selected
  const { data: choiceGroupData, isLoading: isChoiceGroupsLoading } = useAllChoiceGroup(
    shouldFetchChoiceGroups ? memoizedGroupParams : {}
  );


  const storeChoiceGroups: ChoiceGroupOption[] = useMemo(() => {
    if (!choiceGroupData || !Array.isArray(choiceGroupData.data)) return [];
    return choiceGroupData.data
      .map((cg: any) => ({
        id: cg.id || cg.choiceGroupId,
        groupNameEnglish: cg.choiceGroupNameEnglish || cg.groupNameEnglish || '',
        groupNameArabic: cg.choiceGroupNameArabic || cg.groupNameArabic || '',
        price: cg.price ?? cg.choiceGroupPrice ?? 0,
      }))
      .filter((x: ChoiceGroupOption) => !!x.id);
  }, [choiceGroupData]);

  const choiceGroupDataOption = storeChoiceGroups.map((cg) => ({
    value: cg.id,
    label: i18n.language === 'ar' ? cg.groupNameArabic : cg.groupNameEnglish,
  }));

  const handleStoreChange = (storeId: string) => {
    setSelectedStoreId(storeId);
  };

  // Initial choice groups for edit mode
  const initialChoiceGroups: string[] = useMemo(() => {
    if (!mealDataId?.choiceGroupDetails) return [];
    return mealDataId.choiceGroupDetails.map((cg: any) => cg.choiceGroupId).filter(Boolean);
  }, [mealDataId]);

  // Update selectedStoreId when mealDataId changes
  useEffect(() => {
    if (mealDataId?.storeId) {
      setSelectedStoreId(mealDataId.storeId);
    }
  }, [mealDataId?.storeId]);

  // Validation
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        storeId: Yup.string().required(t('meal.form.validation.storeRequired')),

        choiceGroups: Yup.array()
          .of(Yup.string())
          .min(1, t('meal.form.validation.choiceGroupsRequired') || 'Choice groups are required')
          .required(t('meal.form.validation.choiceGroupsRequired') || 'Choice groups are required'),
        mealNameEnglish: Yup.string()
          .required(t('meal.form.validation.mealNameEnglishRequired'))
          .matches(/^[A-Za-z0-9][A-Za-z0-9\s]*$/, 'Meal name should contain letters and numbers')
          .max(100, 'Meal name cannot exceed 100 characters')
          .test('not-only-spaces', 'Meal name cannot be only spaces', (value) => typeof value === 'string' && value.trim().length > 0),
        mealNameArabic: Yup.string()
          .required(t('meal.form.validation.mealNameArabicRequired'))
          .matches(/^[\u0600-\u06FF][\u0600-\u06FF\s]*$/, t('meal.form.validation.arabicOnly'))
          .test('not-only-spaces', 'Meal name cannot be only spaces', (value) => typeof value === 'string' && value.trim().length > 0),
        price: Yup.number()
          .required(t('meal.form.validation.priceRequired'))
          .min(0, t('meal.form.validation.priceMin', { min: 0 }))
          .typeError(t('meal.form.validation.priceNumber')),
        currency: Yup.string().required('Currency is required'),
      }),
    [t]
  );

  const noChoiceGroupsAvailable = storeChoiceGroups.length === 0;

  return (
    <>
      {AllForm({
        initialValues: {
          storeId: mealDataId?.storeId || '',
  
          choiceGroups: initialChoiceGroups,
          mealNameEnglish: mealDataId?.mealNameEnglish || '',
          mealNameArabic: mealDataId?.mealNameArabic || '',
          price: mealDataId?.price ?? 0,
          currency: 'JOD',
        },
        validationSchema,
        validateOnChange: true,
        enableReinitialize: true,
        onSubmit: async (values: any) => {
          const noStoresAvailable = values.storeId && storeOptions.length === 0 && !isStoresLoading;
          if (!mealDataId && noStoresAvailable) return;
          if (!values.storeId) return;
          if (!values.choiceGroups || values.choiceGroups.length === 0) return;

          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          const merchantId = userData?.userId || '';

          const price = Number(values.price);
          if (Number.isNaN(price)) return;

          const modifiedValues = mealDataId
            ? {
                mealNameEnglish: values.mealNameEnglish,
                mealNameArabic: values.mealNameArabic,
                choiceGroups: values.choiceGroups,
                price,
                currency: values.currency,
              }
            : {
                mealNameEnglish: values.mealNameEnglish,
                mealNameArabic: values.mealNameArabic,
                storeId: values.storeId,
                choiceGroups: values.choiceGroups,
                price,
                currency: values.currency,
                merchantId,
              };

          try {
            if (mealDataId) {
              EditMeal(
                { id: mealDataId.mealId || '', data: modifiedValues },
                {
                  onSuccess: () => onCancel(),
                  onError: (error: any) => console.error('Error updating meal:', error),
                }
              );
            } else {
              CreateMeal(
                { modifiedValues },
                {
                  onSuccess: () => onCancel(),
                  onError: (error: any) => console.error('Error creating meal:', error),
                }
              );
            }
          } catch (error) {
            console.error('Error submitting form:', error);
          }
        },

        fields: [
          // Store (restaurant) - single select
          {
            name: 'storeId',
            label: t('meal.form.storeId'),
            type: 'autocomplete',
            isRequired: true,
            component: ({ form }: { field: FieldProps['field']; form: FormikProps<any> }) => (
              <Box sx={{ width: '100%' }}>
                <Autocomplete
                  options={storeOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={storeOptions.find((opt) => opt.value === form.values.storeId) || null}
                  onChange={(_, newValue) => {
                    const storeId = newValue ? newValue.value : '';
                    form.setFieldValue('storeId', storeId);
                    form.setFieldValue('choiceGroups', []);
                    handleStoreChange(storeId);
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('meal.form.placeholders.selectStore')}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Box>
            ),
          },



          // Choice Groups - multi select with FormControl
          {
            name: 'choiceGroups',
            label: t('meal.form.choiceGroups') || 'Choice Groups',
            type: 'hello',
            component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
              const selectedGroupIds: string[] = Array.isArray(field.value)
                ? field.value
                : [];
              const hasError =
                form.touched[field.name] && Boolean(form.errors[field.name]);

              const handleSelectChange = (
                event: SelectChangeEvent<string[]>
              ) => {
                const { value } = event.target;
                const newValue =
                  typeof value === 'string' ? value.split(',') : value;

                form.setFieldTouched(field.name, true, true);
                form.setFieldValue(field.name, newValue, true);

                setOpen(false);
              };

              const handleChipDelete = (groupIdToDelete: string) => () => {
                const newSelectedGroupIds = selectedGroupIds.filter(
                  (groupId) => groupId !== groupIdToDelete
                );
                form.setFieldValue(field.name, newSelectedGroupIds, true);
                form.setFieldTouched(field.name, true, true);
              };

              const selectedChoiceGroups = (choiceGroupDataOption || []).filter(
                (option: any) => selectedGroupIds.includes(option.value)
              );

              const isChoiceGroupSelectDisabled = !form.values.storeId || noChoiceGroupsAvailable;
              const shouldBlur = isChoiceGroupSelectDisabled;
              return (
                <FormControl error={hasError} fullWidth>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Select
                      multiple
                      open={open}
                      onOpen={() => setOpen(true)}
                      onClose={() => setOpen(false)}
                      value={selectedGroupIds}
                      onChange={handleSelectChange}
                      input={<OutlinedInput />}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return (
                            <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
                              {!form.values.storeId
                                ? t('meal.form.placeholders.selectStoreFirst') || 'Select Store First'
                                : noChoiceGroupsAvailable && isChoiceGroupsLoading
                                  ? 'Loading choice groups...'
                                  : noChoiceGroupsAvailable
                                  ? t('meal.form.placeholders.noChoiceGroupsAvailable') || 'No choice groups available'
                                  : t('meal.form.placeholders.choiceGroups') || 'Select choice groups'}
                            </span>
                          );
                        }
                        return '';
                      }}
                      sx={{
                        filter: shouldBlur ? 'blur(1px)' : 'none',
                        backgroundColor: shouldBlur ? '#f5f5f5' : 'inherit',
                        minWidth: 'unset',
                      }}
                      disabled={isChoiceGroupSelectDisabled || isChoiceGroupsLoading}
                    >
                      <MenuItem value="" disabled>
                        <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                          {!form.values.storeId
                            ? `-- ${t('meal.form.placeholders.selectStoreFirst') || 'Select Store First'} --`
                            : isChoiceGroupsLoading
                              ? 'Loading...'
                              : noChoiceGroupsAvailable
                              ? 'No choice groups available'
                              : '-- Select Choice Groups --'}
                        </span>
                      </MenuItem>
                      {!noChoiceGroupsAvailable && form.values.storeId && !isChoiceGroupsLoading && (choiceGroupDataOption || []).map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>

                    {selectedChoiceGroups.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {selectedChoiceGroups.map((group: any) => (
                          <Chip
                            key={group.value}
                            label={group.label}
                            onDelete={handleChipDelete(group.value)}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </FormControl>
              );
            },
          },

          // Price - single numeric field
          {
            name: 'price',
            label: t('meal.form.price') || 'Price',
            type: 'text',
            isRequired: true,
            component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => (
              <TextField
                {...field}
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                placeholder={t('meal.form.placeholders.price') || 'Enter price'}
                error={form.touched[field.name] && Boolean(form.errors[field.name])}
              />
            ),
          },

          // Currency - read-only field
          {
            name: 'currency',
            label: 'Currency',
            type: 'text',
            isRequired: true,
            component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => (
              <TextField
                {...field}
                fullWidth
                disabled
                placeholder="Currency will be auto-filled"
                error={form.touched[field.name] && Boolean(form.errors[field.name])}
              />
            ),
          },

          // Meal names
          {
            name: 'mealNameEnglish',
            label: t('meal.form.mealNameEnglish'),
            type: 'text',
            isRequired: true,
          },
          {
            name: 'mealNameArabic',
            label: t('meal.form.mealNameArabic'),
            type: 'text',
            isRequired: true,
          },
        ],

        customButton: (formikProps: any) => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: COLORS.primary.main,
                '&:hover': { backgroundColor: COLORS.primary.dark },
                '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
              }}
              onClick={(e) => {
                e.preventDefault();
                formikProps.handleSubmit();
              }}
              disabled={CreateMealLoading || EditMealLoading}
            >
              {CreateMealLoading || EditMealLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {mealDataId ? t('meal.form.updating') : t('meal.form.creating')}
                </>
              ) : mealDataId ? (
                t('meal.form.update')
              ) : (
                t('meal.form.create')
              )}
            </Button>

            {!mealDataId && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  formikProps.resetForm();
                  setSelectedStoreId('');
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderColor: COLORS.secondary.main,
                  color: COLORS.secondary.main,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                }}
              >
                {t('meal.form.reset')}
              </Button>
            )}

            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{ bgcolor: warning.main, color: '#fff', '&:hover': { bgcolor: warning.dark, color: '#fff' } }}
            >
              {t('meal.form.cancel')}
            </Button>
          </Box>
        ),
      })()}
    </>
  );
};

export default AddMeal;
